const RECOVERY_KEY = 'billy:chunk-load-recovery'
const RECOVERY_TTL = 5 * 60 * 1000

type RecoveryRecord = { fingerprint: string; attemptedAt: number }
type RecoveryStorage = Pick<Storage, 'getItem' | 'setItem'>

const errorText = (reason: unknown): string => {
    if (typeof reason === 'string') return reason
    if (reason instanceof Error) return `${reason.name}: ${reason.message}`
    if (reason && typeof reason === 'object') {
        const value = reason as { message?: unknown; reason?: unknown; target?: { src?: unknown; href?: unknown } }
        if (typeof value.message === 'string') return value.message
        if (value.reason) return errorText(value.reason)
        if (typeof value.target?.src === 'string') return value.target.src
        if (typeof value.target?.href === 'string') return value.target.href
    }
    return ''
}

export const isChunkLoadError = (reason: unknown) => {
    const message = errorText(reason)
    return /ChunkLoadError|Loading (?:CSS )?chunk [\w-]+ failed|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|\.chunk\.(?:js|css)(?:\?|\)|$)/i.test(message)
}

const fingerprintOf = (reason: unknown) => {
    const message = errorText(reason)
    return message.match(/https?:\/\/[^\s)]+/)?.[0] || message.slice(0, 500)
}

export const recoverFromChunkLoadError = (
    reason: unknown,
    reload: () => void = () => window.location.reload(),
    storage: RecoveryStorage = window.sessionStorage,
    now = Date.now(),
) => {
    if (!isChunkLoadError(reason)) return false
    const fingerprint = fingerprintOf(reason)
    try {
        const saved = storage.getItem(RECOVERY_KEY)
        let previous: RecoveryRecord | null = null
        try {
            previous = saved ? JSON.parse(saved) as RecoveryRecord : null
        } catch {
            // Replace malformed or legacy state below instead of allowing a reload loop.
        }
        if (previous?.fingerprint === fingerprint && now - previous.attemptedAt < RECOVERY_TTL) return false
        const record: RecoveryRecord = { fingerprint, attemptedAt: now }
        storage.setItem(RECOVERY_KEY, JSON.stringify(record))
    } catch {
        // Storage may be disabled in private browsing. A refresh is still the safest recovery.
    }
    reload()
    return true
}

export const importWithChunkRecovery = async <Module>(loader: () => Promise<Module>) => {
    try {
        return await loader()
    } catch (error) {
        if (recoverFromChunkLoadError(error)) throw new Error('A newer version of the dashboard is available. Reloading…')
        throw error
    }
}

let installed = false
export const installChunkLoadRecovery = () => {
    if (installed) return
    installed = true
    window.addEventListener('error', (event) => {
        if (recoverFromChunkLoadError(event.error || event)) event.preventDefault()
    })
    window.addEventListener('unhandledrejection', (event) => {
        if (recoverFromChunkLoadError(event.reason)) event.preventDefault()
    })
}
